'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

// ============================================
// TERMS OF SERVICE LEGAL VERSION
// ============================================
export const LICENSING_TOS_TEXT = `ZyForge grants purchasers a limited, non-exclusive, non-transferable license to use the purchased template for personal or business purposes. All templates, including their underlying code, design, images, and creative elements, remain the sole property of ZyForge.

Purchasers may modify templates for their own use; however, such modifications do not transfer ownership of the original template or any derivative works. Under no circumstances may a purchaser resell, redistribute, sublicense, or otherwise transfer the template or activation keys to any third party, except through the official ZyForge gifting system.

Unauthorized reproduction, distribution, or resale of any ZyForge template is strictly prohibited and may result in license termination, account suspension, and legal action.

ZyForge templates are protected under applicable copyright, intellectual property, and trade dress laws. In cases of infringement, ZyForge or the purchaser may file a DMCA takedown request with the infringing party's hosting provider.

By completing a purchase, the customer acknowledges and agrees to all licensing terms, restrictions, and protections described herein.`;

// ============================================
// TOOLTIP MICROCOPY
// ============================================
export const LICENSING_TOOLTIPS = {
  license: 'Templates are licensed for your use — not sold.',
  nonResale: 'You may not resell or redistribute templates.',
  modifications: 'You can customize it — ownership stays with ZyForge.',
  legal: 'Protected by copyright, DMCA, and licensing laws.',
  gift: 'Send this template to someone else via email.',
};

// ============================================
// MOBILE MICROCOPY
// ============================================
export const LICENSING_MOBILE_COPY = [
  'Licensed, not sold.',
  'No reselling or sharing keys.',
  'Customize freely — ownership stays with ZyForge.',
  'Copyright + DMCA protected.',
  'Gift via email.',
];

// ============================================
// PRODUCT PAGE VERSION
// ============================================
export const LICENSING_PRODUCT_ITEMS = [
  { icon: '✔', color: '#10b981', text: 'Licensed for personal or business use' },
  { icon: '✔', color: '#10b981', text: 'Modify and customize freely' },
  { icon: '✘', color: '#ef4444', text: 'Not for resale or redistribution' },
  { icon: '✔', color: '#10b981', text: 'Protected by copyright & DMCA' },
  { icon: '✔', color: '#10b981', text: 'Activation key required' },
  { icon: '🎁', color: '#8b5cf6', text: 'Gift this template to someone else' },
];

// ============================================
// COMPONENTS
// ============================================

interface LicensingTooltipProps {
  type: keyof typeof LICENSING_TOOLTIPS;
  children: React.ReactNode;
}

export function LicensingTooltip({ type, children }: LicensingTooltipProps) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline-flex items-center gap-1 cursor-help"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <Icon name="InformationCircleIcon" size={13} style={{ color: 'var(--text-muted)' } as React.CSSProperties} />
      {show && (
        <span
          className="absolute bottom-full left-0 mb-2 z-50 px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-lg"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            minWidth: '200px',
          }}
        >
          {LICENSING_TOOLTIPS[type]}
        </span>
      )}
    </span>
  );
}

export function LicensingMobileBadges() {
  return (
    <div className="flex flex-wrap gap-2">
      {LICENSING_MOBILE_COPY.map((text, i) => (
        <span
          key={i}
          className="px-2.5 py-1 rounded-lg text-xs font-medium"
          style={{
            background: 'var(--bg-secondary)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

export function LicensingProductList() {
  return (
    <ul className="space-y-2">
      {LICENSING_PRODUCT_ITEMS.map((item, i) => (
        <li key={i} className="flex items-center gap-2.5 text-sm">
          <span className="text-base flex-shrink-0" style={{ color: item.color }}>
            {item.icon}
          </span>
          <span style={{ color: 'var(--text-secondary)' }}>{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

interface LicensingTOSPanelProps {
  compact?: boolean;
}

export function LicensingTOSPanel({ compact = false }: LicensingTOSPanelProps) {
  const [expanded, setExpanded] = useState(false);

  if (compact) {
    return (
      <div
        className="rounded-xl p-4"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="DocumentTextIcon" size={15} style={{ color: 'var(--accent)' } as React.CSSProperties} />
            <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              License Terms
            </span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs flex items-center gap-1"
            style={{ color: 'var(--accent)' }}
          >
            {expanded ? 'Hide' : 'Read Full Terms'}
            <Icon name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} />
          </button>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Templates are licensed for your use — not sold. You may not resell or redistribute them.
        </p>
        {expanded && (
          <div
            className="mt-3 pt-3 text-xs leading-relaxed whitespace-pre-line"
            style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}
          >
            {LICENSING_TOS_TEXT}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon name="DocumentTextIcon" size={18} style={{ color: 'var(--accent)' } as React.CSSProperties} />
        <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
          License Terms & Legal Protections
        </h3>
      </div>
      <div
        className="text-sm leading-relaxed whitespace-pre-line"
        style={{ color: 'var(--text-secondary)' }}
      >
        {LICENSING_TOS_TEXT}
      </div>
    </div>
  );
}
